// v77.99.23 — Calendar Slice 3 refactor.
//
// EditOccurrenceDialog — the modal that edits a single event (with
// recurrence-scope selection for recurring series).
// Extracted from CalendarPage.jsx (originally ~177 lines inline).
//
// Recurrence-scope semantics (the most important behaviour here):
//   • "single"  → Edit just this date. For a recurring event this
//                 creates a per-date override; for a one-off it
//                 mutates the row in place.
//   • "future"  → Edits this occurrence AND every future one,
//                 splitting the series at this date.
//   • "series"  → Edits the whole series (every occurrence past and
//                 future).
// The radio group above the form only renders for recurring events;
// the form below adapts: RecurrenceFields are shown for "future"
// and "series" but hidden for "single" because changing the cadence
// on a single instance is meaningless.
//
// State coupling:
//   editOccurrence is a single object held in CalendarPage.jsx with
//   the shape:
//       {
//         ev,            // the source event
//         seriesId,      // for recurring rows
//         occurrenceDate,// YYYY-MM-DD of the clicked instance
//         isRecurring,
//         scope,         // "single" | "future" | "series"
//         form: { title, type, parent_id, start, end, child_ids,
//                 notes, is_shared, …recurrence fields, …reminder fields }
//       }
//   The dialog mutates `editOccurrence.form` in place via setter;
//   we never own copies of these fields so submit/cancel paths
//   continue to behave exactly as before.

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import {
  RecurrenceFields,
  ReminderFields,
} from "@/components/calendar/ScheduleControls";
import { Eye, EyeOff, BookOpen, CalendarPlus } from "lucide-react";

const TYPE_LABEL = {
  custody: "Custody",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  school: "School",
  extracurricular: "Extracurricular",
  sports: "Sports",
  holiday: "Holiday",
  call: "Phone call",
  video_call: "Video call",
  other: "Other",
};

export function EditOccurrenceDialog({
  // State
  editOccurrence,
  setEditOccurrence,
  // Reference data
  childList,
  // Helpers
  eligibleParentsFor,
  toggleChildInForm,
  // Actions
  submitEdit,
  cancelOccurrence,
  openLinkedJournal,
  openJournalNote,
  exportEventToCalendar,
  // Context
  isMediator,
}) {
  // Convenience: shallow-update `editOccurrence.form` while keeping the
  // outer scope/isRecurring/ev pointers stable.
  const updateForm = (patch) =>
    setEditOccurrence({
      ...editOccurrence,
      form: { ...editOccurrence.form, ...patch },
    });

  return (
    <Dialog
      open={!!editOccurrence}
      onOpenChange={(v) => !v && setEditOccurrence(null)}
    >
      <DialogContent
        preventOutsideClose
        className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto"
        data-testid="edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Edit event</DialogTitle>
        </DialogHeader>
        <UpgradePrompt
          feature="edit calendar events"
          testid="calendar-edit-upgrade-prompt"
        />
        {editOccurrence && (
          <div className="space-y-3">
            {editOccurrence.isRecurring && (
              <div
                className="border border-[#D9E1E3] bg-white rounded-sm p-3"
                data-testid="edit-scope-group"
              >
                <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] mb-2">
                  Apply changes to
                </div>
                <div
                  role="radiogroup"
                  aria-label="Apply changes to"
                  className="flex flex-col gap-2"
                >
                  {[
                    {
                      v: "single",
                      label: "Only this occurrence",
                      hint: "Edits just this one date",
                    },
                    {
                      v: "future",
                      label: "This and future occurrences",
                      hint: "Splits the series from here",
                    },
                    {
                      v: "series",
                      label: "All events in the series",
                      hint: "Edits every occurrence",
                    },
                  ].map((opt) => {
                    const active = editOccurrence.scope === opt.v;
                    return (
                      <label
                        key={opt.v}
                        className={`flex items-start gap-3 px-3 py-3 border rounded-sm cursor-pointer select-none transition-colors min-h-[44px] ${
                          active
                            ? "bg-[#E8F0F1] border-[#1F9FA4] ring-1 ring-[#1F9FA4]/40"
                            : "bg-white border-[#D9E1E3] hover:bg-[#F5F7F8]"
                        }`}
                        data-testid={`edit-scope-row-${opt.v}`}
                        data-active={active ? "true" : "false"}
                      >
                        <input
                          type="radio"
                          name="edit-scope"
                          value={opt.v}
                          checked={active}
                          onChange={() =>
                            setEditOccurrence({
                              ...editOccurrence,
                              scope: opt.v,
                            })
                          }
                          className="mt-0.5 w-4 h-4 accent-[#1F9FA4] cursor-pointer flex-shrink-0"
                          data-testid={`edit-scope-${opt.v}`}
                        />
                        <span className="flex-1 min-w-0">
                          <span
                            className={`block text-sm ${active ? "font-medium text-[#116B6E]" : "text-[#2E3A3F]"}`}
                          >
                            {opt.label}
                          </span>
                          <span className="block text-xs text-[#6F828A] mt-0.5">
                            {opt.hint}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label>Title</Label>
              <Input
                value={editOccurrence.form.title}
                onChange={(e) => updateForm({ title: e.target.value })}
                data-testid="edit-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select
                  value={editOccurrence.form.type}
                  onValueChange={(v) => updateForm({ type: v })}
                >
                  <SelectTrigger data-testid="edit-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Parent</Label>
                <Select
                  value={editOccurrence.form.parent_id}
                  onValueChange={(v) => updateForm({ parent_id: v })}
                >
                  <SelectTrigger data-testid="edit-parent-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  {/* v77.10 — Restricts to parents linked to the children
                      currently on the edited event. */}
                  <SelectContent>
                    {eligibleParentsFor(editOccurrence.form.child_ids).map(
                      (m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start</Label>
                <Input
                  type="datetime-local"
                  value={editOccurrence.form.start}
                  onChange={(e) => updateForm({ start: e.target.value })}
                  data-testid="edit-start-input"
                />
              </div>
              <div>
                <Label>End</Label>
                <Input
                  type="datetime-local"
                  value={editOccurrence.form.end}
                  onChange={(e) => updateForm({ end: e.target.value })}
                  data-testid="edit-end-input"
                />
              </div>
            </div>
            {childList.length > 0 && (
              <div>
                <Label>Children</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {childList.map((c) => {
                    const active = editOccurrence.form.child_ids.includes(c.id);
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() =>
                          toggleChildInForm(
                            c.id,
                            (list) => updateForm({ child_ids: list }),
                            editOccurrence.form.child_ids,
                          )
                        }
                        className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-sm text-xs transition-colors ${active ? "bg-[#EEF3F4] border-[#2E3A3F] text-[#2E3A3F]" : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#F5F7F8]"}`}
                        data-testid={`edit-child-${c.id}`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: c.color || "#1F9FA4" }}
                        ></span>
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {editOccurrence.scope !== "single" && (
              <RecurrenceFields
                form={editOccurrence.form}
                setForm={(updater) =>
                  setEditOccurrence({
                    ...editOccurrence,
                    form:
                      typeof updater === "function"
                        ? updater(editOccurrence.form)
                        : updater,
                  })
                }
              />
            )}
            <ReminderFields
              form={editOccurrence.form}
              setForm={(updater) =>
                setEditOccurrence({
                  ...editOccurrence,
                  form:
                    typeof updater === "function"
                      ? updater(editOccurrence.form)
                      : updater,
                })
              }
            />
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editOccurrence.form.notes}
                onChange={(e) => updateForm({ notes: e.target.value })}
                data-testid="edit-notes-input"
              />
            </div>
            <div className="flex items-center justify-between border border-[#D9E1E3] rounded-sm p-3">
              <div>
                <div className="text-sm font-medium inline-flex items-center gap-1.5">
                  {editOccurrence.form.is_shared ? (
                    <Eye className="w-3.5 h-3.5 text-[#2E3E48]" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-[#825A41]" />
                  )}
                  Shared with co-parent(s)
                </div>
                <div className="text-xs text-[#6F828A]">
                  {editOccurrence.form.is_shared
                    ? "Visible to your co-parent. Recommended for transparency."
                    : "Private — only you can see this event."}
                </div>
              </div>
              <Switch
                checked={editOccurrence.form.is_shared}
                onCheckedChange={(v) => updateForm({ is_shared: v })}
                data-testid="edit-share-switch"
              />
            </div>
            {editOccurrence?.ev?.linked_journal_count > 0 && (
              <button
                type="button"
                onClick={() => {
                  const ev = editOccurrence.ev;
                  setEditOccurrence(null);
                  openLinkedJournal(ev);
                }}
                className="w-full flex items-center justify-between gap-3 border border-[#A7D8DA] bg-[#E0F2F2] hover:bg-[#D1ECEC] rounded-sm px-3 py-2 transition-colors text-left"
                data-testid="edit-linked-journal-link"
              >
                <span className="inline-flex items-center gap-2 text-sm text-[#116B6E]">
                  <BookOpen className="w-3.5 h-3.5" strokeWidth={1.8} />
                  <span>
                    <span className="font-medium">
                      {editOccurrence.ev.linked_journal_count}
                    </span>{" "}
                    linked journal{" "}
                    {editOccurrence.ev.linked_journal_count === 1
                      ? "entry"
                      : "entries"}
                  </span>
                </span>
                <span className="text-xs text-[#116B6E]/80">View →</span>
              </button>
            )}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          {editOccurrence?.isRecurring && editOccurrence?.scope === "single" && (
            <Button
              variant="outline"
              onClick={cancelOccurrence}
              className="border-[#C0291D] text-[#C0291D] hover:bg-[#FBEFED] rounded-sm"
              data-testid="edit-cancel-occurrence-button"
            >
              Cancel this occurrence
            </Button>
          )}
          {editOccurrence?.ev && !isMediator && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const ev = editOccurrence.ev;
                // Close the edit dialog first so the user lands on Journal cleanly.
                setEditOccurrence(null);
                openJournalNote(ev);
              }}
              className="border-[#D9E1E3] rounded-sm"
              data-testid="edit-journal-note-button"
            >
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Make journal note
            </Button>
          )}
          {editOccurrence?.ev && (
            <Button
              type="button"
              variant="outline"
              onClick={() => exportEventToCalendar(editOccurrence.ev)}
              className="border-[#D9E1E3] rounded-sm"
              data-testid="edit-add-to-calendar-button"
              title="Add to Apple, Google or Outlook calendar"
            >
              <CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Add to calendar
            </Button>
          )}
          <Button
            onClick={submitEdit}
            className="bg-[#1F9FA4] hover:bg-[#178085]"
            data-testid="edit-save-button"
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditOccurrenceDialog;
